import React from 'react';
import { API } from '../api';
import { fmtTime12, fmtDate, fmtLabel, mtLabel, ttLabel } from '../utils/format';
import { normalizeRulesList } from '../utils/rules';
import { matchScoreDisplay } from '../utils/matchScore';
import { compMembershipRestrictionText } from '../utils/membership';
import TrophySvg from './TrophySvg';
import CompRegistrationButton from './CompRegistrationButton';
import PhotoPosterPicker from './PhotoPosterPicker';

function CompetitionHome({ onRegistered, appliedRulesForHome, compPlayers, competitionHomeLeaderboardMinimized, completedMatches, currentUser, displayRulesForHome, handleCompTeamClick, homeRuleDetail, homeRuleTitle, isAdmin, isFinalised, isLoggedIn, leaderboard, matches, openCompSettings, openCompetitionRulesPicker, printCompetitionRules, refreshComp, saveCompetitionAppliedRules, savingHomeRules, selectedComp, setActiveStep, setCompetitionHomeLeaderboardMinimized, setHomeRuleDetail, setHomeRuleTitle, setScoringMatch, setShowRulesPreviewModal, showRulesPreviewModal, teams, totalPlayable }) {
  // Choosing a gallery photo to be this competition's poster.
  const [posterPicking, setPosterPicking] = React.useState(false);
  return (
    <div className="comp-home-wrapper" style={{ marginTop: '8px' }}>
      {posterPicking && (
        <PhotoPosterPicker clubId={selectedComp.ClubId} competitionId={selectedComp.id}
          subjectName={selectedComp.name} onClose={() => setPosterPicking(false)} />
      )}

      {/* Registration button for logged-in players (non-admin) */}
      {isLoggedIn && !isAdmin && !selectedComp.registrationClosed && (
        <CompRegistrationButton API={API} compId={selectedComp.id} currentUser={currentUser} comp={selectedComp} onRegistered={onRegistered} />
      )}



      <div className="comp-home-grid">

        {/* --- Competition Info (left sidebar) --- */}
        <div className="w3-card w3-white" style={{ display: 'flex', flexDirection: 'column', overflow: 'hidden', borderRadius: '12px', minHeight: 0 }}>
          <div className="w3-container w3-padding" style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: '8px' }}>
            {/* Seven facts about the competition. These used to be seven
                stacked pills in seven different colours — theme, theme-l4,
                light-grey, pale-green, pale-yellow, pale-blue and a status
                green — which made a rainbow out of a list where nothing was
                more important than anything else. A label and a value each. */}
            <span className="gs-section-label">Format</span>
            <dl className="comp-facts">
              <dt>Format</dt><dd>{fmtLabel(selectedComp.format)}</dd>
              <dt>Match</dt><dd>{mtLabel(selectedComp.matchType)}</dd>
              <dt>Draw</dt><dd>{ttLabel(selectedComp.tournamentType || 'round-robin')}</dd>
              <dt>Bowls</dt><dd>{selectedComp.bowlsPerPlayer || 4} per player</dd>
              {selectedComp.matchType === 'ends' && (<><dt>Ends</dt><dd>{selectedComp.numberOfEnds}</dd></>)}
              {selectedComp.matchType === 'first-to' && (<><dt>Target</dt><dd>{selectedComp.targetScore}</dd></>)}
              {selectedComp.matchType === 'timed' && (<><dt>Time</dt><dd>{selectedComp.timeLimit} min</dd></>)}
              <dt>Entry</dt>
              <dd>{Number(selectedComp.entryFee) > 0 ? `$${Number(selectedComp.entryFee).toFixed(2)}` : 'Free'}</dd>
              {compMembershipRestrictionText(selectedComp) && (
                <><dt>Open to</dt><dd>{compMembershipRestrictionText(selectedComp)}</dd></>
              )}
              <dt>Players</dt>
              <dd>
                {/* Registered players, not players already placed in a team.
                    Before teams are built the second number is 0, which read
                    as "nobody has entered" on a competition that had entries. */}
                {compPlayers.length}
                {teams.length > 0 && ` · ${teams.length} teams`}
              </dd>
            </dl>
            {/* Status is the one fact here that is a state rather than a
                setting, so it keeps its colour — it is the only thing on the
                panel that can change while you are looking at it. */}
            <span className={`w3-tag w3-round w3-small ${selectedComp.status === 'in-progress' ? 'w3-green' : selectedComp.status === 'completed' ? 'w3-blue' : selectedComp.status === 'reopened' ? 'w3-amber' : 'w3-grey'}`}
              style={{ alignSelf: 'flex-start' }}>
              {selectedComp.status || 'draft'}
            </span>
            <div className="w3-border-top" style={{ paddingTop: '8px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '6px', gap: '6px' }}>
                <strong className="w3-small" style={{ whiteSpace: 'nowrap' }}><i className="fa fa-book w3-text-theme w3-margin-right"></i>Rules</strong>
                <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                  <span className="w3-tag w3-round w3-theme-l5 w3-tiny">{appliedRulesForHome.length}</span>
                </div>
              </div>
              <button
                className="w3-button w3-block w3-round w3-theme-l4 w3-small"
                onClick={() => {
                  setHomeRuleTitle('');
                  setHomeRuleDetail('');
                  setShowRulesPreviewModal(true);
                }}
                style={{ marginTop: '2px' }}
              >
                <i className="fa fa-book w3-margin-right"></i>
                {appliedRulesForHome.length > 0 ? 'View Rules' : 'View Standard Rules'}
              </button>

              {showRulesPreviewModal && (
                <div className="w3-modal" style={{ display: 'block', zIndex: 1900 }} onClick={() => setShowRulesPreviewModal(false)}>
                  <div className="w3-modal-content w3-card-4 w3-round" style={{ maxWidth: '760px', width: 'calc(100% - 20px)' }} onClick={(e) => e.stopPropagation()}>
                    <div className="w3-container w3-theme-l4" style={{ paddingTop: '10px', paddingBottom: '10px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '8px' }}>
                      <h4 style={{ margin: 0, fontSize: '1.05rem' }}><i className="fa fa-book w3-margin-right"></i>Competition Rules</h4>
                      <button className="w3-button w3-small w3-round" onClick={() => setShowRulesPreviewModal(false)}>
                        <i className="fa fa-times"></i>
                      </button>
                    </div>
                    <div className="w3-container" style={{ maxHeight: '62vh', overflowY: 'auto', paddingTop: '10px', paddingBottom: '10px' }}>
                      <div className="w3-small w3-text-grey" style={{ marginBottom: '8px' }}>
                        <strong>{selectedComp.name || 'Competition'}</strong> · {displayRulesForHome.length} rule{displayRulesForHome.length === 1 ? '' : 's'}
                      </div>
                      {appliedRulesForHome.length === 0 && (
                        <div className="w3-panel w3-pale-blue w3-border w3-round" style={{ marginTop: 0 }}>
                          <p className="w3-small" style={{ margin: '8px 0' }}>
                            <i className="fa fa-info-circle w3-margin-right"></i>
                            No custom competition rules selected. Showing standard bowls rules.
                          </p>
                        </div>
                      )}

                      {isAdmin && (
                        <div className="w3-card w3-round w3-border" style={{ marginBottom: '10px' }}>
                          <div className="w3-container" style={{ paddingTop: '8px', paddingBottom: '10px' }}>
                            <div className="w3-small" style={{ fontWeight: 700, marginBottom: '6px' }}>
                              <i className="fa fa-plus-square w3-text-theme w3-margin-right"></i>Add Rule To This Competition
                            </div>
                            <input
                              className="w3-input w3-border w3-round w3-small"
                              placeholder="Rule title"
                              value={homeRuleTitle}
                              onChange={(e) => setHomeRuleTitle(e.target.value)}
                              style={{ marginBottom: '6px' }}
                            />
                            <textarea
                              className="w3-input w3-border w3-round w3-small"
                              placeholder="Optional details"
                              rows={2}
                              value={homeRuleDetail}
                              onChange={(e) => setHomeRuleDetail(e.target.value)}
                            />
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '8px', gap: '8px' }}>
                              <button
                                className="w3-button w3-tiny w3-theme w3-round"
                                disabled={savingHomeRules || !homeRuleTitle.trim()}
                                onClick={async () => {
                                  const title = homeRuleTitle.trim();
                                  if (!title) return;
                                  const newRule = {
                                    id: `club-custom-${Date.now()}`,
                                    title,
                                    detail: homeRuleDetail.trim(),
                                    source: 'club',
                                  };
                                  const next = normalizeRulesList([...(appliedRulesForHome || []), newRule], 'club');
                                  await saveCompetitionAppliedRules(next, 'Rule added');
                                  setHomeRuleTitle('');
                                  setHomeRuleDetail('');
                                }}
                              >
                                <i className="fa fa-plus w3-margin-right"></i>{savingHomeRules ? 'Saving...' : 'Add Rule'}
                              </button>
                              <button
                                className="w3-button w3-tiny w3-light-grey w3-round"
                                onClick={() => {
                                  setShowRulesPreviewModal(false);
                                  openCompetitionRulesPicker('edit');
                                }}
                              >
                                <i className="fa fa-sliders w3-margin-right"></i>Choose From Rules Bank
                              </button>
                            </div>
                          </div>
                        </div>
                      )}

                      {displayRulesForHome.length > 0 ? (
                        <div>
                          {displayRulesForHome.map((r, i) => (
                            <div key={`${r.id}-${i}`} className="w3-card w3-round w3-border" style={{ marginBottom: '8px', overflow: 'hidden' }}>
                              <div className="w3-container" style={{ paddingTop: '8px', paddingBottom: '8px' }}>
                                <div style={{ display: 'flex', alignItems: 'flex-start', gap: '8px' }}>
                                  <span className="w3-tag w3-round w3-theme-l3" style={{ minWidth: '24px', textAlign: 'center' }}>{i + 1}</span>
                                  <div>
                                    <div style={{ fontWeight: 700 }}>{r.title}</div>
                                    {r.detail ? <div className="w3-text-grey w3-small" style={{ marginTop: '2px' }}>{r.detail}</div> : null}
                                  </div>
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <div className="w3-panel w3-pale-yellow w3-border w3-round">
                          <p className="w3-small" style={{ margin: '8px 0' }}><em>No rules available.</em></p>
                        </div>
                      )}
                    </div>
                    <div className="w3-container w3-border-top" style={{ paddingTop: '10px', paddingBottom: '10px', display: 'flex', justifyContent: 'space-between', gap: '8px' }}>
                      <button
                        className="w3-button w3-theme w3-round"
                        disabled={displayRulesForHome.length === 0}
                        onClick={() => printCompetitionRules(selectedComp.name, displayRulesForHome)}
                      >
                        <i className="fa fa-print w3-margin-right"></i>Print Rules
                      </button>
                      <button className="w3-button w3-light-grey w3-round" onClick={() => setShowRulesPreviewModal(false)}>
                        Close
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>
            {/* The player and team counts moved up into the fact list, where
                they belong — they were floated in the middle of the leftover
                space, which is what made this column look mostly empty. */}
            {isAdmin && (
              <button className="w3-button w3-block w3-round w3-theme-l4" style={{ marginTop: 'auto', minHeight: 'var(--gs-control-md)' }}
                title="Use a photo from the gallery as this competition's poster"
                onClick={() => setPosterPicking(true)}>
                <i className="fa fa-image w3-margin-right"></i>Poster from a photo
              </button>
            )}
            {isAdmin && (
              <button className="w3-button w3-block w3-round w3-theme-l4" style={{ minHeight: 'var(--gs-control-md)' }}
                onClick={openCompSettings}>
                <i className="fa fa-cog w3-margin-right"></i>Settings
              </button>
            )}
          </div>
        </div>

        {/* --- Upcoming Matches --- */}
        <div className="w3-card w3-white" style={{ display: 'flex', flexDirection: 'column', overflow: 'hidden', borderRadius: '12px', minHeight: 0 }}>
          <div className="w3-container w3-padding" style={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <h5 style={{ margin: '4px 0' }}><i className="fa fa-clock-o w3-text-amber w3-margin-right"></i>Upcoming Matches</h5>
              {matches.filter(m => !m.completed && m.result !== 'bye' && m.homeTeamId && m.awayTeamId).length > 0 && (
                <button className="w3-button w3-tiny w3-theme-l4 w3-round" onClick={() => setActiveStep('scores')}>
                  Score <i className="fa fa-chevron-right"></i>
                </button>
              )}
            </div>
            {(() => {
              const isDoubleElim = (selectedComp?.tournamentType || 'round-robin') === 'double-elimination';
              const upcoming = matches.filter(m => !m.completed && m.result !== 'bye' && (isDoubleElim ? (m.homeTeamId || m.awayTeamId) : (m.homeTeamId && m.awayTeamId)));
              if (upcoming.length === 0) return <p className="w3-text-grey"><em>No upcoming matches</em></p>;
              const _uniqueRinks = new Set(matches.map(m => m.rink).filter(Boolean));
              const _uniqueGreens = new Set(matches.map(m => m.green).filter(Boolean));
              const _showRink = _uniqueRinks.size > 1;
              const _showGreen = _uniqueGreens.size > 1;
              return (
                <div style={{ flex: 1, overflowY: 'auto' }}>
                  {upcoming.map(m => (
                    <div key={m.id} className="w3-card-2 w3-round w3-hover-shadow"
                      style={{ padding: '8px 12px', marginBottom: '8px', cursor: 'pointer' }}
                      onClick={() => setScoringMatch(m)}>
                      <div className="w3-small" style={{ color: '#555', fontWeight: 600, display: 'flex', alignItems: 'center' }}>
                        <span>{(_showGreen && m.green) ? `G${m.green} ` : ''}{(_showRink && m.rink) ? `R${m.rink}` : ''}{m.time ? `${((_showGreen && m.green) || (_showRink && m.rink)) ? ' · ' : ''}${fmtTime12(m.time)}` : ''}{m.date ? `${((_showGreen && m.green) || (_showRink && m.rink) || m.time) ? ' · ' : ''}${fmtDate(m.date)}` : ''}</span>
                        <span style={{ marginLeft: 'auto', fontSize: 'var(--gs-text-sm)' }}>Round {m.round}</span>
                      </div>
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr auto 1fr', alignItems: 'center', gap: '8px' }}>
                        <strong className="w3-text-theme" style={{ textAlign: 'right' }}>{m.HomeTeam ? m.HomeTeam.name : '?'}</strong>
                        <span className="w3-tag w3-round w3-amber w3-tiny">vs</span>
                        <strong className="w3-text-theme">{m.AwayTeam ? m.AwayTeam.name : '?'}</strong>
                      </div>
                    </div>
                  ))}

                </div>
              );
            })()}
          </div>
        </div>

        {/* --- Played Matches --- */}
        <div className="w3-card w3-white" style={{ display: 'flex', flexDirection: 'column', overflow: 'hidden', borderRadius: '12px', minHeight: 0 }}>
          <div className="w3-container w3-padding" style={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <h5 style={{ margin: '4px 0' }}><i className="fa fa-check-circle w3-text-green w3-margin-right"></i>Played Matches</h5>
              <span className="w3-tag w3-round w3-theme-l3 w3-small">{completedMatches}/{totalPlayable} Scored</span>
            </div>
            {/* Progress bar */}
            <div className="w3-light-grey w3-round" style={{ height: '8px', margin: '8px 0' }}>
              <div className="w3-green w3-round" style={{ height: '8px', width: totalPlayable > 0 ? `${(completedMatches / totalPlayable) * 100}%` : '0%' }}></div>
            </div>
            {(() => {
              const played = matches.filter(m => m.completed && m.result !== 'bye' && m.result !== 'not-played')
                .sort((a, b) => new Date(b.updatedAt) - new Date(a.updatedAt));
              if (played.length === 0) return <p className="w3-text-grey"><em>No matches played yet</em></p>;
              const _pUniqueRinks = new Set(matches.map(m => m.rink).filter(Boolean));
              const _pUniqueGreens = new Set(matches.map(m => m.green).filter(Boolean));
              const _pShowRink = _pUniqueRinks.size > 1;
              const _pShowGreen = _pUniqueGreens.size > 1;
              return (
                <div style={{ flex: 1, overflowY: 'auto' }}>
                  {played.map(m => (
                    <div key={m.id} className={`w3-card-2 w3-round w3-pale-green ${(isAdmin && !isFinalised) ? 'w3-hover-shadow' : ''}`}
                      style={{ padding: '8px 12px', marginBottom: '8px', cursor: 'pointer' }}
                      onClick={() => setScoringMatch(m)}>
                      <div className="w3-small" style={{ color: '#555', fontWeight: 600 }}>
                        R{m.round}{(_pShowGreen && m.green) ? ` · G${m.green}` : ''}{(_pShowRink && m.rink) ? ` R${m.rink}` : ''}
                      </div>
                      {(m.homeScore === 0 && m.awayScore === 0 && (m.result === 'win-home' || m.result === 'win-away')) ? (
                        <div className="w3-center">
                          <span className="w3-tag w3-round w3-deep-orange w3-tiny">
                            <i className="fa fa-flag w3-margin-right"></i>Forfeit — {m.result === 'win-home' ? (m.HomeTeam ? m.HomeTeam.name : 'Home') : (m.AwayTeam ? m.AwayTeam.name : 'Away')} wins
                          </span>
                        </div>
                      ) : (() => {
                        const sc = matchScoreDisplay(m, selectedComp);
                        return (
                        <>
                        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '8px' }}>
                          <strong className={m.result === 'win-home' ? 'w3-text-green' : ''}>{m.HomeTeam ? m.HomeTeam.name : '-'}</strong>
                          <span><strong>{sc.home}</strong> - <strong>{sc.away}</strong></span>
                          <strong className={m.result === 'win-away' ? 'w3-text-green' : ''}>{m.AwayTeam ? m.AwayTeam.name : '-'}</strong>
                        </div>
                        {sc.detail && <div className="w3-center w3-tiny w3-text-grey">{sc.detail}</div>}
                        </>
                        );
                      })()}
                    </div>
                  ))}

                </div>
              );
            })()}
          </div>
        </div>

        {/* --- Leaderboard --- */}
        <div className="w3-card w3-white" style={{ display: 'flex', flexDirection: 'column', overflow: 'hidden', borderRadius: '12px', minHeight: 0 }}>
          <div className="w3-container w3-padding" style={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <h5 style={{ margin: '4px 0' }}><TrophySvg size={16} style={{ marginRight: '8px' }} />Leaderboard</h5>
              <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                {leaderboard && leaderboard.standings && leaderboard.standings.length > 0 && (
                  <button className="w3-button w3-tiny w3-theme-l4 w3-round" onClick={() => { setActiveStep('leaderboard'); refreshComp(); }}>
                    Full Results <i className="fa fa-chevron-right"></i>
                  </button>
                )}
                {leaderboard && leaderboard.standings && leaderboard.standings.length > 0 && (
                  <button className="w3-button w3-tiny w3-theme-l4 w3-round" onClick={() => setCompetitionHomeLeaderboardMinimized(v => !v)} title={competitionHomeLeaderboardMinimized ? 'Expand leaderboard' : 'Minimize leaderboard'}>
                    <i className={`fa fa-${competitionHomeLeaderboardMinimized ? 'plus' : 'minus'} w3-margin-right`}></i>{competitionHomeLeaderboardMinimized ? 'Show' : 'Hide'}
                  </button>
                )}
              </div>
            </div>
            {!competitionHomeLeaderboardMinimized && leaderboard && leaderboard.standings && leaderboard.standings.length > 0 ? (
              <div style={{ flex: 1, overflowY: 'auto' }}>
                {(() => { const noDraws = selectedComp?.matchType === 'first-to' || selectedComp?.tournamentType === 'double-elimination' || selectedComp?.extraEndOnDraw; return (
                <table className="w3-table gs-ladder" style={{ marginTop: '8px' }}>
                  <thead>
                    <tr><th>#</th><th>Team</th><th>P</th><th>W</th>{!noDraws && <th>D</th>}<th>L</th><th>Win%</th><th>Pts</th><th>Diff</th></tr>
                  </thead>
                  <tbody>
                    {/* The top three used to be painted #fff9c4, #e0e0e0 and
                        #ffe0b2 — gold, silver and bronze, three more colours
                        from outside the palette, on a ladder that is not a
                        medal table and is often mid-season. Position is
                        already the first column; it does not need repeating
                        in paint. */}
                    {leaderboard.standings.map((s, i) => (
                      <tr key={s.teamId} className={i < 3 ? 'gs-ladder-top' : ''} style={{ cursor: 'pointer' }}
                        onClick={(e) => handleCompTeamClick(s.teamId, e)}>
                        <td><strong>{s.position}</strong></td>
                        <td><strong>{s.teamName}</strong></td>
                        <td>{s.played}</td>
                        <td>{s.wins}</td>
                        {!noDraws && <td>{s.draws}</td>}
                        <td>{s.losses}</td>
                        <td>{s.played > 0 ? Math.round((s.wins / s.played) * 100) : 0}%</td>
                        <td><strong>{s.gamePoints}</strong></td>
                        <td className={s.pointsDiff > 0 ? 'w3-text-green' : s.pointsDiff < 0 ? 'w3-text-red' : ''}>
                          {s.pointsDiff > 0 ? '+' : ''}{s.pointsDiff}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                ); })()}
              </div>
            ) : competitionHomeLeaderboardMinimized ? (
              <p className="w3-text-grey"><em>Leaderboard minimized</em></p>
            ) : (
              <p className="w3-text-grey"><em>No results yet</em></p>
            )}
          </div>
        </div>

      </div>
    </div>
  );
}

export default CompetitionHome;
